import React, {useEffect, useLayoutEffect, useMemo, useRef, useState} from 'react';
import {ConfigProvider, Justify, SearchBox, Table, Select, TableColumn, Text, Button, Modal, notification} from 'tea-component';
import {autotip, rowtooltip, scrollable, selectable, sortable} from 'tea-component/lib/table/addons';
import {useRequest, useUpdateEffect} from 'ahooks';
import {getLocalText} from '@/pages/datalab/utils/utils';
import {SortableColumn} from 'tea-component/es/table/addons';
import SvgIcon from '@/pages/home/components/SvgIcon';
import './join_select.scss';
import BubbleEllipsis from "@/pages/datalab/components/BubbleEllipsis";
import {getSqlViewScheme, getSqlViewTable} from "@/pages/datalab/pages/Number/SqlView/service";
import {TableHoverFiledSearchBox} from "@/pages/datalab/pages/Home/RecentlyAndShare";
import {useIntl, useDispatch, shallowEqual, useSelector} from "umi";
import { getTableInfoList } from '@dbl/services/dataSource';
import {namespace} from "@/pages/datalab/pages/Multidimensional/Analysis/model";
import { DateGranularity } from '@dbl/core/date';
import {pick} from "lodash-es";
import type { IColumnInfo } from '@dbl/core/dataSource';
import { ChartCalculateMode } from '@dbl/core/chartEditor';
import {LANGUAGE_EN} from "@home/_config/config";

const sortColumns: SortableColumn[] = [{ key: 'create_time_str', prefer: 'desc' }];

interface Paginator {
  pageIndex: number;
  hasNext: boolean;
  total: number;
}

const SqlViewList = (props: { close: Function; commonFormatMessage: Function; isHaveTables: boolean; handleBtnBuriedPoint: Function; openDetailModal: Function; }) => {
  const {
    close,
    commonFormatMessage,
    isHaveTables,
    handleBtnBuriedPoint,
    openDetailModal
  } = props;
  
  const dispatch = useDispatch();
  const { gameCode, save_column_infos: saveColumnInfos, dataTableColumnRawColumns } = useSelector((state: any) => ({
    gameCode: state?.datalab?.gameCode,
    save_column_infos: state[namespace]?.save_column_infos,
    modes: state[namespace]?.mode,
    dataTableColumnRawColumns: state[namespace]?.columnRawColumns,
  }), shallowEqual);
  const {formatMessage, locale} = useIntl();
  const isEn = locale === LANGUAGE_EN;
  const [submitLoading, setSubmitLoading] = useState(false);
  const [sort, setSort] = useState<any>({by: 'create_time_str', order: 'desc'});
  const searchRef = useRef(null);
  // 搜索框点击搜索按钮后, 触发表格请求的搜索字段
  const [searchWords, setSearchWords] = useState<string | undefined>('');
  const paginatorRef = useRef<Paginator>({pageIndex: 1, hasNext: true, total: 0});

  const requestDataRef = useRef(() => {});

  const {loading, error, runAsync} = useRequest(getSqlViewTable, {
    manual: true,
  });
  const [records, setRecords] = useState([]);
  
  const [categoryValue, setCategoryValue] = useState('my'); // 类别筛选的值
  
  const [translatorMap, setTranslatorMap] = useState({}); // 中英文翻译映射
  const [filterData, setFilterData] = useState({
    creators: [],
    labels: [],
    cycles: [],
  });
  
  const [quickValue, setQuickValue] = useState({
    creators: [],
    labels: [],
    cycles: [],
  });
  
  // 根据后端返回的中英映射, 获取文本
  const getText = (key: string, value: string): string => {
    const map = translatorMap?.[key]?.[value];
    return map?.[isEn ? 'en' : 'zh'] || value || "-";
  };
  
  // 请求类目信息
  useEffect(() => {
    // 获取分类数据
    paginatorRef.current = {pageIndex: 1, hasNext: true, total: 0};
    setQuickValue({
      creators: [],
      labels: [],
      cycles: [],
    });
    setSort({by: 'create_time_str', order: 'desc'});
    const params = {
      game_code: gameCode,
      keywords: searchWords,
      category: categoryValue,
    };
    getSqlViewScheme(params).then((res: any) => {
      setFilterData(res || {});
      setTranslatorMap(res?.trans || {});
    }).catch((e) => {
      console.log('SQL视图 过滤器数据请求错误: ', e);
    });
  }, [categoryValue]);
  

  requestDataRef.current = (isAdd: boolean) => {
    const params = {
      game_code: gameCode,
      category: categoryValue,
      chart_names: [],
      keywords: searchWords,
      page: paginatorRef.current.pageIndex,
      page_size: 20,
      sorts: [{key: sort.by, value: sort.order}],
      ...quickValue,
    };

    runAsync(params)
      .then((res) => {
        const dataList = res?.data_list || [];
        if (dataList.length > 0) {
          setRecords((records) => isAdd ? records.concat(dataList) : dataList);
          paginatorRef.current.total = res?.row_count;
        }
      })
      .catch((e) => {
        console.log(e);
      }).finally(() => {
        paginatorRef.current.hasNext = true;
      });
  };
  
  
  useUpdateEffect(() => {
    paginatorRef.current = {pageIndex: 1, hasNext: true, total: 0};
    requestDataRef.current(false);
    setRecords([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameCode, searchWords, JSON.stringify(sort), quickValue]);
  
  const {creatorsFilterData, cyclesFilterData, labelsFilterData } = useMemo(() => {
    const { creators: creatorsFilter, cycles: cyclesFilter, labels: labelsFilter } = filterData;
    const getFilterData = (data: string[]) => data?.map(item => ({ value: item, text: item || "-" }));
    return {
      creatorsFilterData: getFilterData(creatorsFilter),
      cyclesFilterData: getFilterData(cyclesFilter)?.map(item => ({ value: item?.value, text: getText('cycle', item?.text) })),
      labelsFilterData: getFilterData(labelsFilter)?.map(item => ({ value: item?.value, text: getText('label', item?.text) })),
    };
  }, [filterData, translatorMap, isEn]);
  
  useLayoutEffect(() => {
    const element = document.querySelector('.add-source-table .tea-table__body');
    const handleScroll = () => {

      // 检查是否滚动到底部
      if (element?.scrollTop + element?.clientHeight >= element?.scrollHeight - 10) {

        if (paginatorRef.current.hasNext && (paginatorRef.current.pageIndex * 20 < paginatorRef.current.total)) {
          paginatorRef.current = {
            pageIndex: paginatorRef.current.pageIndex + 1,
            hasNext: false,
            total: paginatorRef.current.total
          };
          requestDataRef.current(true);
        }

      }
    };

    // 添加滚动监听事件
    element?.addEventListener('scroll', handleScroll);

    return () => {
      element?.removeEventListener('scroll', handleScroll);
    };

  }, []);
  
  // 列定义
  const columns: TableColumn[] = [
    {
      key: 'table_name',
      header: formatMessage({id: "dbl_dataSource_table_name"}),
      width: 310,
      fixed: 'left',
      render: (record: any) => {
        const name = getLocalText(isEn, record?.customize_name, record?.customize_name_en, record?.table_name);
        return (
          <>
            <span className={'tea-text-overflow'} title={name}>{name}</span>
            <Button type={'link'} onClick={() => {
              openDetailModal({
                resetProps: {
                  title: name,
                  isSingle: true,
                },
                tableIds: [record.table_id],
                tableInfos: [{
                  connect_type: '',
                  customize_name: record.table_name,
                  customize_name_en: record.table_name,
                  granularity: record.cycle,
                  on: [],
                  table_code: 't0',
                  table_id: record.table_id,
                  table_name: record.table_name,
                }],
                dataTables: [{
                  table_id: record.table_id,
                  table_code: 't0',
                  driver: record.driver,
                  database_uuid: record.database_uuid,
                  cycle: record.cycle,
                  kind: "virtual"
                }],
              });
            }}>{commonFormatMessage('detail')}</Button>
          </>
        );
      },
    },
    {
      key: 'table_desc',
      header: formatMessage({id: "dbl_dataSource_table_description"}),
      width: 356,
      render: (record: any) => {
        const desc = (isEn ? record.table_desc_en || record.table_desc : record.table_desc);
        return desc ? (
          <BubbleEllipsis className={'add-source-table-desc-bubble'} content={desc}/>
        ) : '-';
      },
    },
    {
      key: 'creator',
      header: <div key={JSON.stringify(quickValue?.creators)}>
        <TableHoverFiledSearchBox {
          ...{
            data: creatorsFilterData,
            handleOnClickCreateSearch: (value: string[]) => setQuickValue({...quickValue, creators: value}),
            value: quickValue?.creators,
            buttonText: formatMessage({id: "dbl_sql_view_creator"}),
          }
        } />
      </div>,
      width: 150,
      render(record: any) {
        return <BubbleEllipsis content={record?.creator}>
          <Text style={{ width: '100%' }} overflow>
            {record?.creator}
          </Text>
        </BubbleEllipsis>;
      },
    },
    {
      key: 'create_time_str',
      header: formatMessage({id: 'dbl_sql_view_creator_time'}),
      width: 190,
    },
    {
      key: 'label',
      header: <div key={JSON.stringify(quickValue?.labels)}>
        <TableHoverFiledSearchBox {
          ...{
            data: labelsFilterData,
            handleOnClickCreateSearch: (value: string[]) => setQuickValue({...quickValue, labels: value}),
            value: quickValue?.labels,
            buttonText: formatMessage({id: 'dbl_sql_view_label'}),
          }
        } />
      </div>,
      width: 120,
      render(record) {
        const label = getText('label', record?.label);
        // const label = record?.label;
        return <BubbleEllipsis content={label}>
          <Text style={{ width: '100%' }} overflow>
            {label}
          </Text>
        </BubbleEllipsis>;
      },
    },
    {
      key: 'cycle',
      // header: formatMessage({id: 'dbl_sql_view_cycle'}),
      header: <div key={JSON.stringify(quickValue?.cycles)}>
        <TableHoverFiledSearchBox {
          ...{
            data: cyclesFilterData,
            handleOnClickCreateSearch: (value: string[]) => setQuickValue({...quickValue, cycles: value}),
            value: quickValue?.cycles,
            buttonText: formatMessage({id: 'dbl_sql_view_cycle'}),
          }
        } />
      </div>,
      width: isEn ? 150 : 120,
      render(record) {
        return getText('cycle', record?.cycle);
        // return record?.cycle;
      },
    },
  ];
  
  const [selectTable, setSelectTable] = useState(null);
  
  const filterOptions = useMemo(() => {
    const options = [
      {
        value: "all",
        text: "全部",
        textEn: "All",
      },
      {
        value: "my",
        text: `我的SQL视图`,
        textEn: `My SQL View`,
      },
      // {
      //   value: "share",
      //   text: `与我分享`,
      //   textEn: `Shared with Me`,
      // },
      {
        value: "others",
        text: `其他`,
        textEn: `Others`,
      }
    ];
    return options.map(opt => ({value: opt.value, text: isEn ? opt.textEn : opt.text}));
  }, [isEn]);
  
  return (
    <>
      <Modal.Body>
        <Justify
          left={(<Select
            appearance="button"
            matchButtonWidth
            button={(<div className="select-result-button">
              <span className="select-result-button-label">{formatMessage({id: 'dbl_sql_view_category'})}</span>
            &nbsp;
              <span className="select-result-button-content">
                {filterOptions.find(opt => opt.value === categoryValue)?.text || ''}
              </span>
            </div>)}
            options={filterOptions}
            onChange={(value) => {setCategoryValue(value)}}
            value={categoryValue}
            style={{width: 214}}
          />)}
          right={(<SearchBox
            searchBoxRef={searchRef}
            className="add-steep-search"
            multiline={false}
            value={searchWords}
            placeholder={formatMessage({id: 'dbl_sql_view_search'})}
            onPressEnter={(value) => {
              setSearchWords(value);
            }}
            onChange={(value) => {
              setSearchWords(value);
            }}
            onSearch={(value) => {
              setSearchWords(value);
            }}
            onClear={() => {
              setSearchWords('');
            }}
          />)}
      
        />
      
        <ConfigProvider locale={isEn ? "en" : "zh"}>
          <Table
            records={records}
            bordered
            className="add-source-table"
            recordKey="table_id"
            columns={columns}
            rowDisabled={(record) => record.sync_status === 1}
            addons={[
            // 支持表格滚动，高度超过 192 开始显示滚动条
              scrollable({
                maxHeight: selectTable?.tableId ? 477 : 522,
              }),
              selectable({
              // 选框放在「消息类型」列上
                targetColumnKey: 'table_name',
                // 禁用全选
                all: false,
                // 已选中的键值
                value: selectTable?.tableId ? [selectTable.tableId] : [],
                // 选中键值发生变更
                onChange: (value, context) => {
                  // 用户只能选一个
                  if (value[0]) {
                    const { table_name: tableName, cycle, database_uuid, driver} = context?.record || {};
                    setSelectTable({
                      tableName,
                      tableId: value[0],
                      cycle,
                      database_uuid,
                      driver
                    });
                  } else {
                    setSelectTable(null);
                  }
                },
                // false=>禁用 true=>开启
                rowSelectable: (rowKey: string) => selectTable?.tableId ? rowKey === selectTable?.tableId : true,
              }),
              sortable({
                columns: sortColumns,
                value: [sort],
                onChange: (value: any[], context) => {
                // 如果value数组没有元素说明当前是未排序状态, 那么上传给后端的参数就应该是按照创建时间降序排序
                  const sort = value?.length > 0 ? context.sort : {by: 'table_name', order: undefined};
                  setSort(sort);
                },
              }),
              autotip({
                isLoading: loading,
                isError: !!error,
                loadingText: commonFormatMessage("loading"),
                errorText: (
                  <span>
                    {commonFormatMessage("load_fail")}
                  </span>
                ),
                emptyText: commonFormatMessage("no_data"),
                style: {lineHeight: '482px'},
              }),
              rowtooltip({
                tooltip: (record) =>
                  record.sync_status === 1 ? (
                    <div style={{padding: "10px 14px"}}>{formatMessage({id: 'dbl_dataSource_tooltip'})}</div>) : null,
              }),
            ]}
          />
        </ConfigProvider>
        
        {
          selectTable?.tableId ? (<div className={'footer-flex join-select-sortable'}>
            <div className="data-label-tips join-mgr-modal-select-cell-container">
              <div className="footer-item">
                <div className={`footer-text footer-not-num`} title={selectTable.tableName ?? ''}>
                  <span className={'tea-text-overflow'}>{selectTable.tableName ?? ''}</span>
                  <Button type={'link'} onClick={() => {
                    openDetailModal({
                      resetProps: {
                        title: selectTable.tableName ?? '',
                        isSingle: true,
                      },
                      tableIds: [selectTable.tableId],
                      tableInfos: [{
                        connect_type: '',
                        customize_name: selectTable.tableName,
                        customize_name_en: selectTable.tableName,
                        granularity: selectTable.cycle,
                        on: [],
                        table_code: 't0',
                        table_id: selectTable.tableId,
                        table_name: selectTable.tableName,
                      }],
                      dataTables: [{
                        table_id: selectTable.tableId,
                        table_code: 't0',
                        driver: selectTable.driver,
                        database_uuid: selectTable.database_uuid,
                        cycle: selectTable.cycle,
                        kind: "virtual"
                      }],
                    });
                  }}>{commonFormatMessage('detail')}</Button>
                </div>
                <div
                  onClick={() => {
                    // TODO 删除选择的 sql视图
                    setSelectTable(null);
                  }}
                >
                  <SvgIcon type="clonse" className="footer-icon"/>
                </div>
              </div>
            </div>
          </div>) : ''
        }
      
      
      </Modal.Body>
      <Modal.Footer className="add-Source-footer">
        <Button
          type="primary"
          loading={submitLoading}
          /* eslint-disable-next-line @typescript-eslint/no-misused-promises */
          onClick={async () => {
            if (isHaveTables) {
              // 已存在数据源的情况下, 修改数据源, 进行提示
              const result = await Modal.confirm({
                message: formatMessage({id: 'dbl_dataSource_confirm_replace_title'}),
                description: (<div
                  dangerouslySetInnerHTML={{__html: formatMessage({id: 'dbl_dataSource_confirm_replace_info'})}}/>),
                okText: formatMessage({id: 'dbl_common_confirm'}),
                cancelText: formatMessage({id: 'dbl_common_cancel'}),
                className: 'dbl-datasource-confirm',
                maskStyle: {zIndex: 1001},
              });
              
              if (!result) return;
            }
            
            setSubmitLoading(true);
            getTableInfoList([selectTable?.tableId], gameCode).then((res) => {
              const sqlViewInfo = res?.[0] || {};
              dispatch({
                type: `${namespace}/setDataTableJoinRelations`,
                payload: {
                  dataTableJoinRelations: [{
                    connect_type: "",
                    granularity: sqlViewInfo?.cycle ?? DateGranularity.DAY,
                    on: [],
                    only_one_table: true,
                    table_code: "t0",
                    table_id: sqlViewInfo?.table_id,
                    table_name: sqlViewInfo?.table_name,
                    customize_name: sqlViewInfo?.customize_name,
                    customize_name_en: sqlViewInfo?.customize_name_en,
                  }],
                  isOpenSelfJoin: false,
                },
              });
              const tableIds = [sqlViewInfo?.table_id];
              
              // 针对sql模式下的缓存，更细粒度的处理
              const fullSelectedColumnsArray = sqlViewInfo?.column_list.map((task: { name: string; })=>task?.name);
              if (saveColumnInfos && saveColumnInfos[sqlViewInfo?.table_id]) {
                saveColumnInfos[sqlViewInfo?.table_id] = Object.fromEntries(
                  Object.entries(saveColumnInfos[sqlViewInfo?.table_id]).filter(([key]) =>
                    fullSelectedColumnsArray.includes(key),
                  ),
                );
              }
              dispatch({
                type: `${namespace}/setTableInfo`,
                payload: { response: res, save_column_infos: pick(saveColumnInfos, [
                  ...tableIds,
                  'saveRowLimits',
                  'sorts',
                  'chartsCfgSave',
                ]) },
                callback: (dataTables, mode) => {
                  try {
                    // eslint-disable-next-line @typescript-eslint/naming-convention
                    const {column_class_list} = dataTables[0];
                    const columnRawColumns: IColumnInfo[] = [];
                    column_class_list.forEach((item) => {
                      item.columns.forEach(column => columnRawColumns.push({...column}));
                    });
                    const fullColumnRawColumns = dataTableColumnRawColumns?.filter((task: { column_custom: boolean; })=>task?.column_custom);
                    if (mode === ChartCalculateMode.RawColumn) {
                      dispatch({
                        type: `${namespace}/sqlSelectDataSetClear`,
                      });
                      dispatch({
                        type: `${namespace}/switchChartCalculateMode`,
                        payload: {
                          tables: dataTables,
                          mode,
                          columnRawColumns: [...columnRawColumns, ...fullColumnRawColumns],
                          // TODO: Sql模式自定义字段需去除???
                          // customColumns: [],
                        },
                      });
                      
                    } else {
                      dispatch({
                        type: `${namespace}/filterConditionByTable`,
                        payload: {
                          table_ids: tableIds,
                          tables: dataTables,
                          isSqlSchemaAggregation: true,
                          forceAssignEmptyHashAnalysis: true
                        },
                      });
                    }
                  } catch (e) {
                    console.error(e);
                  }
                }
              });
              notification.success({
                description: formatMessage({id: "dbl_dataSource_add_table"})
              });
              handleBtnBuriedPoint([{tableId: sqlViewInfo?.table_id, tableName: sqlViewInfo?.table_name}]);
              close();
            }).catch((err) => {
              console.log('err', err);
            }).finally(() => {
              setSubmitLoading(false);
            });
          }}
          disabled={!selectTable?.tableId}
        >
          {formatMessage({id: 'dbl_dataSource_submit'})}
        </Button>
      </Modal.Footer>
    </>
  );
};

export default SqlViewList;
